import type { User } from "@supabase/supabase-js";
import type { Tenant } from "@/lib/types";
import { Bell, CreditCard, CheckCircle, AlertTriangle, Gift } from "lucide-react";

interface Props {
  tenant: Tenant;
  user: User;
}

const PLACEHOLDER_NOTIFICATIONS = [
  {
    id: "1",
    icon: CreditCard,
    iconBg: "bg-primary/10",
    iconColor: "text-primary",
    title: "Card design submitted",
    description: "Your custom card design has been submitted for review.",
    time: "Just now",
    read: false,
  },
  {
    id: "2",
    icon: CheckCircle,
    iconBg: "bg-success/10",
    iconColor: "text-success",
    title: "Payment method saved",
    description: "Your card details have been securely saved.",
    time: "2 hours ago",
    read: false,
  },
  {
    id: "3",
    icon: Gift,
    iconBg: "bg-accent/10",
    iconColor: "text-accent-foreground",
    title: "Referral recorded",
    description: "A friend joined using your code.",
    time: "1 day ago",
    read: true,
  },
  {
    id: "4",
    icon: AlertTriangle,
    iconBg: "bg-warning/10",
    iconColor: "text-warning",
    title: "Action required",
    description: "Please complete your profile to speed up card delivery.",
    time: "2 days ago",
    read: true,
  },
  {
    id: "5",
    icon: Bell,
    iconBg: "bg-muted",
    iconColor: "text-muted-foreground",
    title: "Welcome to the waitlist!",
    description: `You've joined the ${"{tenant}"} waitlist. We'll notify you when your card is ready.`,
    time: "3 days ago",
    read: true,
  },
];

const UserNotifications = ({ tenant, user }: Props) => {
  const notifications = PLACEHOLDER_NOTIFICATIONS.map((n) => ({
    ...n,
    description: n.description.replace("{tenant}", tenant.brandName),
  }));

  return (
    <div className="max-w-lg mx-auto animate-fade-in">
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-lg font-bold font-display">Notifications</h2>
        <p className="text-xs text-muted-foreground">Stay updated on your card and account</p>
      </div>

      <div className="divide-y">
        {notifications.map((n) => {
          const Icon = n.icon;
          return (
            <div
              key={n.id}
              className={`flex items-start gap-3 px-4 py-3.5 transition-colors ${!n.read ? "bg-primary/[0.03]" : ""}`}
            >
              <div className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center ${n.iconBg}`}>
                <Icon className={`h-4 w-4 ${n.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm leading-tight ${!n.read ? "font-semibold" : "font-medium"}`}>
                    {n.title}
                  </p>
                  {!n.read && (
                    <div
                      className="h-2 w-2 shrink-0 rounded-full mt-1"
                      style={{ backgroundColor: `hsl(${tenant.primaryColor})` }}
                    />
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{n.description}</p>
                <p className="text-[11px] text-muted-foreground/60 mt-1">{n.time}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-4 py-8 text-center">
        <p className="text-xs text-muted-foreground">You're all caught up!</p>
      </div>
    </div>
  );
};

export default UserNotifications;
